import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { Avatar, TierBadge, RateBar, SkeletonRows, ErrorMessage, usePageMeta } from '../components/ui';
import { deriveTier, TIER_RANK, brawlerMeta } from '../data/meta';

function TierList() {
  usePageMeta({
    title: 'Brawl Stars Tier List',
    description: 'Every Brawl Stars brawler ranked by win rate and pick rate from tracked battles, with tiers updated as the meta shifts.',
  });
  const navigate = useNavigate();
  const [brawlers, setBrawlers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ key: 'win', dir: -1 });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getBrawlers('win_rate');
      setBrawlers(
        data.map((b) => ({
          ...b,
          win: b.win_rate != null ? parseFloat(b.win_rate) : null,
          pick: b.pick_rate != null ? parseFloat(b.pick_rate) : null,
          tier: deriveTier(b.win_rate),
          meta: brawlerMeta(b.brawler_name),
        }))
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const movers = useMemo(
    () => brawlers.slice().sort((a, b) => (b.pick ?? 0) - (a.pick ?? 0)).slice(0, 6),
    [brawlers]
  );

  const rows = useMemo(() => {
    let list = brawlers;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((b) => b.brawler_name.toLowerCase().includes(q));
    }
    const get = (b) => {
      if (sort.key === 'tier') return TIER_RANK[b.tier];
      if (sort.key === 'name') return b.brawler_name.toLowerCase();
      if (sort.key === 'battles') return b.total_battles ?? 0;
      return b[sort.key] ?? -1;
    };
    return list.slice().sort((a, b) => {
      const av = get(a);
      const bv = get(b);
      if (typeof av === 'string') return av.localeCompare(bv) * sort.dir;
      return (av - bv) * sort.dir;
    });
  }, [brawlers, search, sort]);

  const th = (key, label, cls = '') => (
    <th
      className={`sortable ${cls}`}
      onClick={() => setSort((s) => ({ key, dir: s.key === key ? -s.dir : -1 }))}
    >
      {label}
      {sort.key === key && <span className="arr">{sort.dir < 0 ? '▼' : '▲'}</span>}
    </th>
  );

  return (
    <div className="page">
      <div className="wrap">
        <div className="phead">
          <div>
            <div className="eyebrow">Meta · live data</div>
            <h1>Tier List</h1>
            <div className="sub">
              The competitive meta as a ladder, ranked by win rate across all tracked battles.
              Click any brawler for its best maps and stats.
            </div>
          </div>
        </div>

        <div className="filters">
          <input
            className="search-input"
            placeholder="🔎 Search brawlers…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="select"><span className="lab">Ranked by</span>win rate</div>
        </div>

        {error ? (
          <ErrorMessage message={error} onRetry={load} />
        ) : loading ? (
          <SkeletonRows rows={12} />
        ) : (
          <>
            <div className="section-title"><span className="mk">📈</span> Most picked right now</div>
            <div className="trend-strip">
              {movers.map((b) => (
                <div key={b.brawler_id} className="trend-card" onClick={() => navigate(`/brawlers/${b.brawler_id}`)}>
                  <Avatar name={b.brawler_name} />
                  <div>
                    <div className="nm">{b.brawler_name}</div>
                    <div className="meta">
                      <span className="trend up">{b.pick != null ? `${b.pick.toFixed(1)}%` : '—'}</span>{' '}
                      <span style={{ color: 'var(--muted)', fontWeight: 600 }}>pick</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="tablecard" style={{ marginTop: 26 }}>
              <div className="table-scroll">
              <table className="meta">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>#</th>
                    {th('name', 'Brawler')}
                    {th('tier', 'Tier')}
                    {th('win', 'Win %')}
                    {th('pick', 'Pick %', 'col-pick')}
                    {th('battles', 'Battles', 'col-map')}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((b, i) => (
                    <tr key={b.brawler_id} onClick={() => navigate(`/brawlers/${b.brawler_id}`)}>
                      <td className="rank">{i + 1}</td>
                      <td>
                        <div className="bcell">
                          <Avatar name={b.brawler_name} />
                          <div>
                            <div className="bname">{b.brawler_name}</div>
                            {b.meta && <div className="brole">{b.meta.role} · {b.meta.rarity}</div>}
                          </div>
                        </div>
                      </td>
                      <td><TierBadge tier={b.tier} /></td>
                      <td><RateBar value={b.win} max={60} lime /></td>
                      <td className="col-pick"><RateBar value={b.pick} max={22} /></td>
                      <td className="col-map"><span className="num-cell">{(b.total_battles ?? 0).toLocaleString()}</span></td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr><td colSpan={6} className="empty-state">No brawlers match “{search}”.</td></tr>
                  )}
                </tbody>
              </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default TierList;
